// Copyright 2018 Sorama B.V.

import { autoinject, BindingEngine, Disposable } from "aurelia-framework";
import { IWebRequest, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IWorkflowModuleDeactivate } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { MotionDeviceDiscoveryService } from "services/MotionDevice/MotionDeviceDiscoveryService";

class ApiCall {
    public name: string;
    public description: string;
    public httpType: string;
    public url: string;
    public input?: string;
    public output?: string;
    public defaultInput?: unknown;
}

/**
 * MODULE OPERATION DESCRIPTION - MeasureModule
 * The Measure Module provides functionality for performing measurements.
 */
@autoinject
export class MotionRestApiModule implements IWorkflowModuleDeactivate {
    //#region Other Properties
    private subscriptions: Disposable[] = [];

    private selectedApi: string;

    private deviceName = "name";
    private returnMessage: string;

    public apiCalls: ApiCall[] = [
        {
            name: "Connected Devices",
            description:
                "Get all available devices from the API. 'id' and 'name' fields in the output should be the same. 'range' defines the range of the motion device in meters.",
            url: "",
            httpType: "get",
            output: "[{id:string,name:string,range:{x:number,y:number,z:number}}]",
        },
        {
            name: "Stop Device",
            description:
                "Stop the selected device. 'name' should be the name, returned by the 'Connected Devices' call.",
            url: "{name}/Stop",
            httpType: "put",
        },
        {
            name: "Home Device",
            description:
                "Home the selected device. 'name' should be the name, returned by the 'Connected Devices' call.",
            url: "{name}/Home",
            httpType: "put",
        },
        {
            name: "Go To Position",
            description:
                "Move the selected device to the designated position (in meters). 'name' should be the name, returned by the 'Connected Devices' call.The 'Execute Call' will send {x:0,y:0,z:0}.",
            url: "{name}/Position",
            input: "{x:number,y:number,z:number}",
            defaultInput: { x: 0, y: 0, z: 0 },
            httpType: "put",
        },
        {
            name: "Get Position",
            description:
                "Get the current position (in meters) of the selected device. 'name' should be the name, returned by the 'Connected Devices' call. This call is optional.",
            url: "{name}/Position",
            output: "{x:number,y:number,z:number}",
            httpType: "get",
        },
    ];

    //#endregion

    constructor(
        private readonly shellViewManager: ShellViewManager,
        private readonly bindingEngine: BindingEngine,
        private readonly motionDiscoveryService: MotionDeviceDiscoveryService,
        private readonly webrequestHandler: IWebRequestHandler
    ) {}

    //#region Lifecycle functions
    public activate(): void {}

    public deactivateModule(): void {
        while (this.subscriptions.length) this.subscriptions.pop().dispose();
    }
    //#endregion

    public executeCall(call: ApiCall): void {
        this.returnMessage = "Executing call..";

        const url = this.selectedApi + "/" + call.url.replace("{name}", this.deviceName);

        let request: IWebRequest<unknown>; // Promise<TypedHttpResponseMessage<unknown>>;

        if (call.httpType == "get") {
            request = this.webrequestHandler.createGetRequest(url);
        } else if (call.httpType == "put") {
            if (call.input && call.defaultInput) {
                request = this.webrequestHandler.createPutRequest(url, call.defaultInput);
            } else {
                request = this.webrequestHandler.createPutRequest(url);
            }
        }

        request
            .send()
            .then((x) => {
                this.returnMessage = "Ok:" + JSON.stringify(x.content);
            })
            .catch((x) => {
                this.returnMessage = "Failed (" + x.statusCode + "): " + x.message;
            });
    }
}
